import React from "react";
import { getGroupsData } from '../assets/data.js';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function useScheduleCalendar({
  lessons, selectedFilter,
  currentDate, setCurrentDate,
  viewMode,
  students, groups, isLoadingStudentsGroups,
  setSelectedDate,
}) {
  const getLessonsForDate = (date) => {
    const filteredLessons = lessons.filter(lesson => {
      try {
        // Проверяем, что lesson.date существует и является валидной датой
        if (!lesson.date) {
          return false;
        }

        let lessonDate;
        // Handle both ISO string and date string formats
        if (lesson.date.includes('T')) {
          // ISO string format
          lessonDate = new Date(lesson.date);
        } else {
          // Date string format (e.g., "Mon Dec 25 2023")
          lessonDate = new Date(lesson.date);
        }

        // Проверяем, что дата валидна
        if (isNaN(lessonDate.getTime())) {
          return false;
        }

        // Проверяем фильтр по студентам
        if (selectedFilter !== 'all') {
          // Фильтруем уроки по выбранному студенту или группе
          if (lesson.type === 'lesson') {
            if (selectedFilter.startsWith('student-')) {
              const studentId = selectedFilter.replace('student-', '');
              if (lesson.studentId !== studentId) {
                return false;
              }
            } else if (selectedFilter.startsWith('group-')) {
              const groupId = selectedFilter.replace('group-', '');
              if (lesson.groupId !== groupId) {
                return false;
              }
            }
          }
        }
        // События показываем всегда, независимо от фильтра студента

        // Сравниваем только даты (год, месяц, день) без учета времени
        const dateMatch = lessonDate.getFullYear() === date.getFullYear() &&
               lessonDate.getMonth() === date.getMonth() &&
               lessonDate.getDate() === date.getDate();

        return dateMatch;
      } catch (error) {
        console.warn('Error comparing lesson date:', error);
        return false;
      }
    });

    return filteredLessons;
  };

  // Генерация календаря на месяц
  const generateMonthCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const calendar = [];
    let week = [];
    // Смещение для недели, начинающейся с понедельника
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // 0 - Mon, 6 - Sun
    // Добавляем пустые ячейки перед первым числом месяца
    for (let i = 0; i < firstDayOfWeek; i++) {
      week.push({
        date: null,
        isCurrentMonth: false,
        isToday: false,
        lessons: []
      });
    }
    const currentDateObj = new Date(firstDay);
    while (currentDateObj <= lastDay) {
      const date = new Date(currentDateObj);
      const lessonsForDate = getLessonsForDate(date);
      week.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === new Date().toDateString(),
        lessons: lessonsForDate
      });
      if (week.length === 7) {
        calendar.push(week);
        week = [];
      }
      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }
    // Если остались дни в последней неделе — дополняем пустыми ячейками
    if (week.length > 0) {
      while (week.length < 7) {
        week.push({
          date: null,
          isCurrentMonth: false,
          isToday: false,
          lessons: []
        });
      }
      calendar.push(week);
    }
    return calendar;
  };

  // Генерация календаря на неделю
  const generateWeekCalendar = () => {
    const currentDateObj = new Date(currentDate);

    // Находим понедельник текущей недели
    const dayOfWeek = currentDateObj.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Если воскресенье, то отнимаем 6 дней
    currentDateObj.setDate(currentDateObj.getDate() - daysToSubtract);

    const week = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDateObj);
      const lessonsForDate = getLessonsForDate(date);

      week.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === new Date().toDateString(),
        lessons: lessonsForDate
      });

      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }

    return [week]; // Возвращаем массив с одной неделей
  };

  // Генерация календаря на день
  const generateDayCalendar = () => {
    const date = new Date(currentDate);
    const lessonsForDate = getLessonsForDate(date);

    // Создаем массив с одним днем, но с правильным индексом дня недели
    const dayIndex = (date.getDay() + 6) % 7; // 0 = Monday, 6 = Sunday
    const week = Array(7).fill(null).map((_, i) => {
      if (i === dayIndex) {
        return {
          date,
          isCurrentMonth: true,
          isToday: date.toDateString() === new Date().toDateString(),
          lessons: lessonsForDate
        };
      } else {
        return {
          date: null,
          isCurrentMonth: false,
          isToday: false,
          lessons: []
        };
      }
    });

    return [week];
  };

  // Получение календаря в зависимости от режима просмотра
  const getCalendarData = () => {
    switch (viewMode) {
      case 'week':
        return generateWeekCalendar();
      case 'day':
        return generateDayCalendar();
      default:
        return generateMonthCalendar();
    }
  };

  // Генерация временных слотов
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) { // Start from 0 (12 AM)
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      slots.push({
        hour,
        time: `${displayHour} ${ampm}`,
        isHour: true
      });
    }
    return slots;
  };

  // Получение позиции урока во времени
  // eslint-disable-next-line no-unused-vars
  const getLessonPosition = (lesson) => {
    // Предполагаем, что уроки начинаются в 9:00 и длятся 1 час
    // В реальном приложении это должно браться из данных урока
    const startHour = 9; // 9:00 AM
    const duration = 1; // 1 час

    const top = startHour * 60; // позиция в пикселях
    const height = duration * 60; // высота в пикселях

    return { top, height };
  };

  // eslint-disable-next-line no-unused-vars
  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handlePrevPeriod = () => {
    switch (viewMode) {
      case 'week':
        setCurrentDate(prev => {
          const newDate = new Date(prev);
          newDate.setDate(newDate.getDate() - 7);
          return newDate;
        });
        break;
      case 'day':
        setCurrentDate(prev => {
          const newDate = new Date(prev);
          newDate.setDate(newDate.getDate() - 1);
          return newDate;
        });
        break;
      default: // month
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  };

  const handleNextPeriod = () => {
    switch (viewMode) {
      case 'week':
        setCurrentDate(prev => {
          const newDate = new Date(prev);
          newDate.setDate(newDate.getDate() + 7);
          return newDate;
        });
        break;
      case 'day':
        setCurrentDate(prev => {
          const newDate = new Date(prev);
          newDate.setDate(newDate.getDate() + 1);
          return newDate;
        });
        break;
      default: // month
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  };

  const getStudentName = (studentId, groupId) => {
    // Если данные еще загружаются, показываем индикатор вместо "Unknown"
    if (isLoadingStudentsGroups) {
      return '...';
    }

    if (groupId) {
      // Используем состояние groups (загружено из Supabase)
      const group = groups && (groups[groupId] || groups[groupId.toString()] || groups[parseInt(groupId)]);
      if (group) return group.name;
      // Fallback на localStorage
      const localGroups = getGroupsData();
      const localGroup = localGroups && (localGroups[groupId] || localGroups[groupId.toString()] || localGroups[parseInt(groupId)]);
      return localGroup ? localGroup.name : 'Unknown Group';
    }
    if (!studentId) {
      return 'Unknown Student';
    }

    // Используем состояние students (загружено из Supabase)
    // studentId может быть UUID или slug
    let student = null;

    // Пробуем найти по ключу объекта (slug)
    if (students[studentId]) {
      student = students[studentId];
    } else {
      // Пробуем найти по полям внутри объектов
      student = Object.values(students).find(s => {
        if (!s) return false;
        // Проверяем разные форматы ID
        return (
          s.id === studentId ||
          s.invite_slug === studentId ||
          s.invite_slug === studentId.replace(/^-+/, '') || // Убираем дефисы в начале
          s.slug === studentId ||
          s.studentId === studentId
        );
      });
    }

    // Если все еще не нашли, пробуем найти по ключу без дефисов в начале
    if (!student && studentId.startsWith('-')) {
      const cleanId = studentId.replace(/^-+/, '');
      student = students[cleanId] || Object.values(students).find(s =>
        s && (s.invite_slug === cleanId || s.slug === cleanId)
      );
    }

    return student ? (student.name || student.studentName || 'Student') : 'Unknown Student';
  };

  // Форматировать время, убирая секунды (HH:mm:ss -> HH:mm)
  const formatTime = (timeString) => {
    if (!timeString) return '9:00';
    // Если время в формате HH:mm:ss, убираем секунды
    if (timeString.includes(':') && timeString.split(':').length === 3) {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    }
    return timeString;
  };

  // Get student name with time for display
  const getStudentNameWithTime = (lesson) => {
    if (lesson.type !== 'lesson') return lesson.title || 'Untitled Item';

    const studentName = getStudentName(lesson.studentId, lesson.groupId);
    if (lesson.allDay) {
      return (
        <>
          {studentName}
          <span style={{ color: '#888', fontSize: '0.9em', marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
            All Day
          </span>
        </>
      );
    }

    const fromTime = formatTime(lesson.from || lesson.timeFrom);
    const toTime = formatTime(lesson.to || lesson.timeTo);
    const timeText = `${fromTime} - ${toTime}`;
    return (
      <>
        {studentName}
        <span style={{ color: '#888', fontSize: '0.9em', marginLeft: '4px' }}>{timeText}</span>
      </>
    );
  };

  // Получение заголовка в зависимости от режима просмотра
  const getCalendarHeader = () => {
    switch (viewMode) {
      case 'week':
        const weekStart = new Date(currentDate);
        const dayOfWeek = weekStart.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        weekStart.setDate(weekStart.getDate() - daysToSubtract);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      case 'day':
        return currentDate.toLocaleDateString();
      default:
        return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  return {
    getLessonsForDate,
    getCalendarData,
    generateTimeSlots,
    getLessonPosition,
    handleDateClick,
    handlePrevPeriod,
    handleNextPeriod,
    getStudentName,
    formatTime,
    getStudentNameWithTime,
    monthNames,
    dayNames,
    getCalendarHeader,
  };
}
